import {
	Component, OnInit, OnChanges, SimpleChanges, OnDestroy} from '@angular/core';
import { UntypedFormBuilder, FormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { MonitorPolicy } from 'DiscoveryEngine.TS/Monitors';
import { MonitorConfigComponentBase } from '../MonitorConfigComponentBase';
import { NetFacadeService, NavigationService } from 'netview';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { HashSet, StringUtils } from 'DiscoveryEngine.TS/Utils';
import { ConfigurationService } from 'configuration';
import { GetSlackInfoResultDto } from 'UvExplorerServer.Dtos.TS/Tools';
import { ResultDto } from 'UvExplorerServer.Dtos.TS';

@Component({
	selector: 'monitor-events',
	templateUrl: './monitorevents.component.html',
	styleUrls: ['./monitorevents.component.scss']
})
export class MonitorEventsComponent extends MonitorConfigComponentBase implements OnInit, OnChanges, OnDestroy {

	public EventOnValues: UntypedFormControl;
	public LogEvents: UntypedFormControl;
	public EmailEvents: UntypedFormControl;
	public EmailRecipients: UntypedFormControl;
	public IncludeSystemRecipients: UntypedFormControl;
	public SystemRecipients: UntypedFormControl;
	public EmailEventsAsText: UntypedFormControl;
	public EmailSeparateEvents: UntypedFormControl;
	public SlackEvents: UntypedFormControl;
	public SlackWebhooks: UntypedFormControl;

	private _SystemEmailRecipients: string = "";
	public IsEmailConfigured: boolean = false;

	public EventOnMap = [
		{ label: "Up", value: "0" },
		{ label: "Down", value: "1" },
		{ label: "Warning", value: "2" },
		{ label: "Unknown", value: "3" },
		{ label: "Information", value: "4" },
	];

	constructor(private monService: MonitorService,
		private configService: ConfigurationService,
		private messageService: MessageService,
		private navService: NavigationService,
		private netFacadeService: NetFacadeService,
		private fb: UntypedFormBuilder) {

		super();
	}

	public ngOnInit(): void {

	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (this.visible) {
			this.GetSmtpConfiguration();
			this.BuildForm();
		}
		else {
			this.IsEmailConfigured = false;
			this._SystemEmailRecipients = "";

			this.BuildForm();
		}
	}

	public ngOnDestroy(): void {

	}

	private _GetEventOnLabelValue(label: string): string {
		for (let i = 0; i < this.EventOnMap.length; ++i) {
			if (this.EventOnMap[i].label === label) {
				return this.EventOnMap[i].value;
			}
		}
		console.assert(false);
		return "";
	}

	private _GetEventOnValueLabel(value: string): string {
		for (let i = 0; i < this.EventOnMap.length; ++i) {
			if (this.EventOnMap[i].value === value) {
				return this.EventOnMap[i].label;
			}
		}
		console.assert(false);
		return "";
	}

	private GetSmtpConfiguration(): void {

		this.IsEmailConfigured = false;
		this._SystemEmailRecipients = "";

		let netViewGuid: string = this.navService.location.netViewGuid;
		if (netViewGuid) {
			let netView: NetViewDto = this.netFacadeService.findNetwork(netViewGuid);
			if (!!netView && netView.HasSmtpSettings && netView.SmtpSettings.IsValid) {
				this.IsEmailConfigured = true;
				this._SystemEmailRecipients = netView.SmtpSettings.ToAddress;
			}
		}
	}

	public BuildForm(): void {
		let eventOnValues = [];
		let logEvents = false;
		let emailEvents = false;
		let emailRecipients = "";
		let includeSystemRecipients = false;
		let systemRecipients = "";
		let emailEventsAsText = false;
		let emailSeparateEvents = false;
		let slackEvents = false;
		let slackWebhooks = "";

		if (!!this.editor && this.editor.HasPolicy) {

			let monPolicy: MonitorPolicy = this.editor.Policy;

			if (monPolicy.HasEmailOnMonitorStates) {
				let eventOnLabels: string[] = StringUtils.CsvToStringArray(monPolicy.EmailOnMonitorStates);
				eventOnLabels.forEach((label: string) => {
					eventOnValues.push(this._GetEventOnLabelValue(label));
				});
			}

			logEvents = monPolicy.LogEvents;
			emailEvents = monPolicy.EmailEvents;
			emailRecipients = monPolicy.EmailRecipients;
			includeSystemRecipients = monPolicy.EmailSystemRecipients;
			systemRecipients = this._SystemEmailRecipients;
			emailEventsAsText = monPolicy.EmailEventsAsText;
			emailSeparateEvents = monPolicy.EmailSeparateEvents;
			slackEvents = monPolicy.SlackEvents;
			slackWebhooks = monPolicy.SlackWebhooks;
		}

		this.EventOnValues = new UntypedFormControl(eventOnValues);
		this.LogEvents = new UntypedFormControl(logEvents);
		this.EmailEvents = new UntypedFormControl(emailEvents);
		this.EmailRecipients = new UntypedFormControl(emailRecipients);
		this.IncludeSystemRecipients = new UntypedFormControl(includeSystemRecipients);
		this.SystemRecipients = new UntypedFormControl(systemRecipients);
		this.EmailEventsAsText = new UntypedFormControl(emailEventsAsText);
		this.EmailSeparateEvents = new UntypedFormControl(emailSeparateEvents);
		this.SlackEvents = new UntypedFormControl(slackEvents);
		this.SlackWebhooks = new UntypedFormControl(slackWebhooks);

		this.enableDisableControls();

		this.SettingsForm = this.fb.group({
			EventOnValues: this.EventOnValues,
			LogEvents: this.LogEvents,
			EmailEvents: this.EmailEvents,
			EmailRecipients: this.EmailRecipients,
			IncludeSystemRecipients: this.IncludeSystemRecipients,
			SystemRecipients: this.SystemRecipients,
			EmailEventsAsText: this.EmailEventsAsText,
			EmailSeparateEvents: this.EmailSeparateEvents,
			SlackEvents: this.SlackEvents,
			SlackWebhooks: this.SlackWebhooks,
		});
	}

	public enableDisableControls(): void {
		this.enableDisableFormControl(this.EmailRecipients, this.EnableMonitorEmailRecipients);
		this.enableDisableFormControl(this.SlackWebhooks, this.EnableMonitorSlackWebhooks);
		this.enableDisableFormControl(this.IncludeSystemRecipients, this.EnableMonitorIncludeSystemRecipients);
		this.enableDisableFormControl(this.SystemRecipients, false);
		this.enableDisableFormControl(this.EmailEventsAsText, this.EnableMonitorEmailEventsAsText);
		this.enableDisableFormControl(this.EmailSeparateEvents, this.EnableMonitorEmailSeparateEvents);

		if (this.SlackEvents.value) {
			this.SlackWebhooks.enable();
		}
		else {
			this.SlackWebhooks.disable();
		}
	}

	public get EnableMonitorEmailRecipients(): boolean {
		if (!!this.EmailEvents) {
			return this.EmailEvents.value;
		}
		else {
			return false;
		}
	}

	public get EnableMonitorSlackWebhooks(): boolean {
		if (!!this.SlackWebhooks) {
			return this.SlackWebhooks.value;
		}
		else {
			return false;
		}
	}

	public get EnableMonitorIncludeSystemRecipients(): boolean {
		if (!!this.EmailEvents) {
			return this.EmailEvents.value;
		}
		else {
			return false;
		}
	}

	public get EnableMonitorEmailEventsAsText(): boolean {
		if (!!this.EmailEvents) {
			return this.EmailEvents.value;
		}
		else {
			return false;
		}
	}

	public get EnableMonitorEmailSeparateEvents(): boolean {
		if (!!this.EmailEvents) {
			return this.EmailEvents.value;
		}
		else {
			return false;
		}
	}

	public applyTempChanges(): void {
		if (!!this.editor && this.editor.HasPolicy) {

			let monPolicy: MonitorPolicy = this.editor.Policy;

			monPolicy.EmailOnMonitorStates = "";
			if (!!this.EventOnValues.value) {
				let eventOnLabels: string[] = [];
				this.EventOnValues.value.forEach((value: string) => {
					let label = this._GetEventOnValueLabel(value);
					eventOnLabels.push(label);
				});
				monPolicy.EmailOnMonitorStates = StringUtils.StringArrayToCsv(eventOnLabels);
			}

			monPolicy.LogEvents = this.LogEvents.value;
			monPolicy.EmailEvents = this.EmailEvents.value;
			monPolicy.EmailRecipients = this.EmailRecipients.value;
			monPolicy.EmailSystemRecipients = this.IncludeSystemRecipients.value;
			monPolicy.EmailEventsAsText = this.EmailEventsAsText.value;
			monPolicy.EmailSeparateEvents = this.EmailSeparateEvents.value;
			monPolicy.SlackEvents = this.SlackEvents.value;
			monPolicy.SlackWebhooks = this.SlackWebhooks.value;

			this.BuildForm();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Events"];
	}

	public get enableSlackChannelSelector(): boolean {
		return this.SlackEvents.value;
	}

	public selectedChannelsChanged(): void {
		if (this.SettingsForm) {
			this.SettingsForm.markAsDirty();
		}
	}

	public get enableTestSlackButton(): boolean {
		return (!!this.SlackEvents && this.SlackEvents.value && !!this.SlackWebhooks && this.SlackWebhooks.value);
	}

	public testSlack(): void {
		if (!this.editor.HasPolicy) {
			this.messageService.displayErrorMessage("No policy attached to this editor");
			return;
		}
		this.configService.testSlackConnection(this.SlackWebhooks.value).subscribe(
			() => {
				this.messageService.displayInfoMessage("Successfully sent message to all channel webhooks.");
			},
			(error) => {
				this.messageService.displayErrorMessage(error);
			});
	}

}
